import { isGeoBrowseEnabled } from '@/config/geoBrowse';

export type ProductDemoFeatureId =
  | 'search'
  | 'aida'
  | 'advanced-search'
  | 'enrichment'
  | 'api'
  | 'sticky-notes'
  | 'geo-browse';

export type ProductDemoFeature = {
  id: ProductDemoFeatureId;
  label: string;
  tagline: string;
  bullets: string[];
  infoHref: string;
  toolHref: string;
  toolExternal?: boolean;
  toolLabel?: string;
};

const ALL_PRODUCT_DEMO_FEATURES: ProductDemoFeature[] = [
  {
    id: 'search',
    label: 'Search',
    tagline: 'From a query to association evidence — including filters, columns, favorites, and downloads.',
    bullets: [
      'Type a gene, disease, variant, or chemical and pick a match.',
      'Switch tabs (GDAs, VDAs, Variants, Genes, Diseases, Chemicals).',
      'Use Export & tools for downloads, manage columns (pin/drag/hide), and open row details.',
    ],
    infoHref: '/docs',
    toolHref: '/examples',
    toolLabel: 'Try live examples',
  },
  {
    id: 'aida',
    label: 'AIDA',
    tagline: 'Ask biomedical questions in natural language.',
    bullets: [
      'Chat interface with recent history and new-chat controls.',
      'Answers grounded in DISGENET associations and evidence.',
      'Switch conversations from the sidebar history.',
    ],
    infoHref: '/Tools?section=assistant',
    toolHref: '/Assistant',
  },
  {
    id: 'advanced-search',
    label: 'Advanced Search',
    tagline: 'Resolve lists of identifiers before searching.',
    bullets: [
      'Choose Diseases, Genes, Variants, or Chemicals.',
      'Paste a list, map identifiers, and review matches.',
      'Search selected items or (for variants) send them to Enrichment.',
    ],
    infoHref: '/advancedSearch',
    toolHref: '/advancedSearch',
  },
  {
    id: 'enrichment',
    label: 'Enrichment',
    tagline: 'Disease enrichment analysis for gene or variant sets.',
    bullets: [
      'Paste a set and choose ORA / DSEA analysis methods.',
      'Configure sources, score thresholds, and overlap filters.',
      'Explore ranked diseases with plots and exportable tables.',
    ],
    infoHref: '/Tools?section=enrichment',
    toolHref: '/enrichment',
  },
  {
    id: 'api',
    label: 'REST API',
    tagline: 'Try endpoints in the interactive console.',
    bullets: [
      'Browse GET/POST endpoints by resource group.',
      'Authorize with your API key and load example parameters.',
      'Inspect JSON responses before wiring production clients.',
    ],
    infoHref: '/Tools?section=console',
    toolHref: '/interactive-console',
  },
  {
    id: 'sticky-notes',
    label: 'Sticky Notes',
    tagline: 'Capture insights while exploring Search + results.',
    bullets: [
      'Use sticky notes to keep ideas next to the evidence you’re reviewing.',
      'Add and color-code notes without interrupting your workflow.',
      'Restore minimized notes when you want to revisit details later.',
    ],
    infoHref: '/Profile-area#stickyNotes',
    toolHref: '/examples?view=DISEASES&idents=C0028754&source=ALL&tab=GDA',
    toolLabel: 'Try Sticky Notes',
  },
  {
    id: 'geo-browse',
    label: 'ExpressNET',
    tagline: 'Gene expression across diseases.',
    bullets: [
      'Browse diseases, genes, and studies with adjustable padj / log2FC thresholds.',
      'Drill into study detail with volcano plots, PCA, top DEGs, and DISGENET enrichment.',
      'Navigate cross-tab from disease → studies → study detail, or gene → gene detail.',
    ],
    infoHref: '/geo-browse',
    toolHref: '/geo-browse',
    toolLabel: 'Open ExpressNET',
  },
];

export const PRODUCT_DEMO_FEATURES: ProductDemoFeature[] = ALL_PRODUCT_DEMO_FEATURES.filter(
  feature => feature.id !== 'geo-browse' || isGeoBrowseEnabled,
);
